import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableStatic';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { Box } from '@uhg-abyss/web/ui/Box';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import React, { useMemo, useState } from 'react';

import configuration from './includes/configuration.json';

/**
 * Table component for displaying IRE record details.
 * Supports masking sensitive columns (DOB, SSN, Subscriber SSN) and custom cell rendering.
 */
export function Table({
  dobRemediationFindings = {},
  rows = [],
  ssnRemediationFindings = {},
  trustedRecordSources = [],
}) {
  const [maskedColumns, setMaskedColumns] = useState(['dob', 'ssn', 'subscriberSsn']);

  /** Renders a masked or unmasked cell value based on column masking. */
  const renderMaskedCell = ({ cell, column }) => {
    return <span>{maskedColumns.includes(column.id) ? '*'.repeat(10) : cell.value}</span>;
  };

  /**
   * Renders a cell for DOB/SSN/Subscriber SSN columns, applying trusted/untrusted highlighting and masking.
   * @param {Object} args - Cell render arguments
   * @param {Object} findings - Remediation findings
   * @param {Array<Object>} trustedSources - Trusted sources
   */
  const renderPIICell = (args, findings, trustedSources) => {
    const trustedValue = findings?.trustedValue;
    const trustedSourceMatch = trustedSources?.find((item) => {
      return item.source === args?.row?.original?.dataSource && item.recordId === args?.row?.original?.recordId;
    });
    const trusted = trustedSourceMatch && trustedValue === args?.cell?.value;
    const untrusted = (findings?.untrustedRecordSources ?? []).some((item) => {
      return (
        item.source === args?.row?.original?.dataSource &&
        item.recordId === args?.row?.original?.recordId &&
        item.value === args?.cell?.value
      );
    });

    let color = '';
    if (trusted) {
      color = 'var(--abyss-colors-alert-color-background-container-success)';
    } else if (untrusted) {
      color = 'var(--abyss-colors-alert-color-background-container-error)';
    }

    return (
      <Box color={color} css={{ marginLeft: '-10px', marginRight: '-10px', padding: 0, position: 'absolute' }}>
        <Layout.Group css={{ height: '100%', marginLeft: '6px', marginRight: '6px' }}>
          {renderMaskedCell(args)}
        </Layout.Group>
      </Box>
    );
  };

  /** Renders the cell for the Last Seen column as a formatted date string. */
  const renderCellLastSeen = ({ cell }) => {
    return dayjs(cell.value).format('MM/DD/YYYY - HH:mm:ss');
  };

  /** Renders the cell for the Address column as an address element. */
  const renderCellAddress = ({ cell }) => {
    return (
      <address style={{ fontStyle: 'normal' }}>
        <div style={{ wordBreak: 'break-word' }}>{cell.value}</div>
      </address>
    );
  };

  /** Toggles masking for a given column by its ID. */
  function handleMaskedColumnToggle(columnId) {
    setMaskedColumns((prev) => {
      return prev.includes(columnId)
        ? prev.filter((col) => {
            return col !== columnId;
          })
        : [...prev, columnId];
    });
  }

  /** Renders the header for masked columns with a toggle icon. */
  const renderMaskedHeader = (args) => {
    return (
      <Layout.Group alignItems="middle" grow space={0}>
        <div>{args?.column?.name}</div>
        <IconSymbol
          css={{
            position: 'absolute',
            right: 'calc(var(--abyss-space-lg) / 2 + var(--abyss-space-md) )',
            zIndex: '999999999',
          }}
          icon={maskedColumns.includes(args?.column?.id) ? 'visibility' : 'visibility_off'}
          onClick={() => {
            return handleMaskedColumnToggle(args?.column?.id);
          }}
        />
      </Layout.Group>
    );
  };

  /**
   * Memoized columns configuration for the table.
   * Applies custom renderers and masking logic.
   */
  const columns = useMemo(() => {
    return configuration?.initialColumns.map((item) => {
      const column = { ...item };
      if (column.Header === 'DOB') {
        column.Header = renderMaskedHeader;
        column.Cell = (args) => {
          return renderPIICell(args, dobRemediationFindings, trustedRecordSources);
        };
      } else if (column.Header === 'SSN') {
        column.Header = renderMaskedHeader;
        column.Cell = (args) => {
          return renderPIICell(args, ssnRemediationFindings, trustedRecordSources);
        };
      } else if (column.Header === 'Subscriber SSN') {
        column.Header = renderMaskedHeader;
        column.Cell = (args) => {
          return renderPIICell(args, ssnRemediationFindings, trustedRecordSources);
        };
      } else if (column.Header === 'Last Seen') {
        column.Cell = renderCellLastSeen;
      } else if (column.Header === 'Address') {
        column.Cell = renderCellAddress;
      }
      return column;
    });
  }, [maskedColumns, dobRemediationFindings, ssnRemediationFindings, trustedRecordSources]);

  return (
    <ErrorHandler location="src/widgets/RiskRecords/IreRecordDetail/components/Table/Table.jsx">
      <TableComponent
        accessor="eidSearch-IreRecordDetail"
        columns={columns}
        configuration={configuration}
        rows={rows}
      />
    </ErrorHandler>
  );
}
